import { useCallback, useEffect, useState } from "react";
import { Plus, UserCheck, UserX } from "lucide-react";
import {
  fetchEmployees,
  upsertEmployee,
  deactivateEmployee,
  reactivateEmployee,
} from "../tipsApi";
import type { Employee, EmployeeRole } from "../types";
import { ROLE_META } from "../roleMeta";

type Filter = "all" | "active" | "inactive";
type RoleFilter = "all" | EmployeeRole;

function roleLabel(r: RoleFilter): string {
  return r === "all" ? "All roles" : ROLE_META[r].label;
}

export function EmployeesView() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>("active");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [formName, setFormName] = useState("");
  const [formRoles, setFormRoles] = useState<EmployeeRole[]>(["bartender"]);
  const [formStartDate, setFormStartDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchEmployees();
      setEmployees(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = employees.filter((e) => {
    if (filter === "active" && !e.is_active) return false;
    if (filter === "inactive" && e.is_active) return false;
    if (roleFilter !== "all" && !e.roles.includes(roleFilter)) return false;
    return true;
  });

  function openAdd() {
    setEditId(null);
    setFormName("");
    setFormRoles(["bartender"]);
    setFormStartDate(new Date().toISOString().slice(0, 10));
    setShowForm(true);
  }

  function openEdit(emp: Employee) {
    setEditId(emp.id);
    setFormName(emp.name);
    setFormRoles([...emp.roles]);
    setFormStartDate(emp.start_date ?? "");
    setShowForm(true);
  }

  function toggleFormRole(role: EmployeeRole) {
    setFormRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  }

  async function handleSave() {
    if (!formName.trim() || formRoles.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await upsertEmployee({
        ...(editId ? { id: editId } : {}),
        name: formName.trim(),
        roles: formRoles,
        start_date: formStartDate || null,
      } as any);
      setShowForm(false);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(emp: Employee) {
    setError(null);
    try {
      if (emp.is_active) {
        const ok = confirm(
          `Deactivate ${emp.name}? They will no longer appear for new tip entry. Past pay periods and reports keep their records.`,
        );
        if (!ok) return;
        await deactivateEmployee(emp.id);
      } else {
        await reactivateEmployee(emp.id);
      }
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Employees</h2>
          <p className="mt-0.5 text-xs text-tribal-muted">
            Deactivating hides someone from new tip entry. Past pay periods and reports keep their history.
          </p>
        </div>
        <button
          type="button"
          onClick={openAdd}
          className="flex items-center gap-1.5 rounded-lg bg-tribal-red px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-tribal-red/80"
        >
          <Plus className="h-4 w-4" /> Add Employee
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-2 text-xs">
        {(["active", "inactive", "all"] as Filter[]).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`rounded-full px-3 py-1 capitalize transition-colors ${
              filter === f
                ? "bg-tribal-red/20 text-white"
                : "bg-white/5 text-tribal-muted hover:bg-white/10"
            }`}
          >
            {f}
          </button>
        ))}
        <span className="mx-1 self-center text-tribal-border">|</span>
        {(["all", "bartender", "axe_coach", "kitchen", "front_desk"] as RoleFilter[]).map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRoleFilter(r)}
            className={`rounded-full px-3 py-1 transition-colors ${
              roleFilter === r
                ? "bg-tribal-red/20 text-white"
                : "bg-white/5 text-tribal-muted hover:bg-white/10"
            }`}
          >
            {roleLabel(r)}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-900/20 px-3 py-2 text-sm text-red-400">{error}</div>
      )}

      {/* Inline form */}
      {showForm && (
        <div className="mb-4 rounded-lg border border-tribal-border bg-tribal-surface p-4">
          <h3 className="mb-3 text-sm font-semibold">{editId ? "Edit Employee" : "New Employee"}</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_auto]">
            <div>
              <label className="mb-1 block text-xs text-tribal-muted">Name</label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="w-full rounded-md border border-tribal-border bg-tribal-bg px-3 py-1.5 text-sm focus:border-tribal-red focus:outline-none"
                autoFocus
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-tribal-muted">Roles</label>
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {(Object.keys(ROLE_META) as EmployeeRole[]).map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => toggleFormRole(r)}
                    className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                      formRoles.includes(r)
                        ? ROLE_META[r].badgeCls
                        : "bg-white/5 text-tribal-muted hover:bg-white/10"
                    }`}
                  >
                    {ROLE_META[r].label}
                  </button>
                ))}
              </div>
              {formRoles.length === 0 && (
                <p className="mt-1 text-[10px] text-red-400">Select at least one role</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs text-tribal-muted">Start Date</label>
              <input
                type="date"
                value={formStartDate}
                onChange={(e) => setFormStartDate(e.target.value)}
                className="w-full rounded-md border border-tribal-border bg-tribal-bg px-3 py-1.5 text-sm focus:border-tribal-red focus:outline-none"
              />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !formName.trim() || formRoles.length === 0}
              className="rounded-md bg-tribal-red px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-tribal-red/80 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="rounded-md bg-white/5 px-4 py-1.5 text-sm text-tribal-muted hover:bg-white/10"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <p className="text-sm text-tribal-muted">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-tribal-muted">No employees match the current filters.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-tribal-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-tribal-border bg-tribal-surface text-left text-xs text-tribal-muted">
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Roles</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Start</th>
                <th className="px-4 py-2 font-medium">End</th>
                <th className="px-4 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((emp) => (
                <tr
                  key={emp.id}
                  className="border-b border-tribal-border last:border-0 hover:bg-white/[0.02]"
                >
                  <td className="px-4 py-2 font-medium">{emp.name}</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {emp.roles.map((r) => (
                        <span key={r} className={`rounded-full px-2 py-0.5 text-xs ${ROLE_META[r].badgeCls}`}>
                          {ROLE_META[r].label}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    {emp.is_active ? (
                      <span className="flex items-center gap-1 text-xs text-emerald-400">
                        <UserCheck className="h-3.5 w-3.5" /> Active
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-tribal-muted">
                        <UserX className="h-3.5 w-3.5" /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-tribal-muted">{emp.start_date ?? "—"}</td>
                  <td className="px-4 py-2 text-tribal-muted">{emp.end_date ?? "—"}</td>
                  <td className="px-4 py-2">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(emp)}
                        className="text-xs text-tribal-muted underline hover:text-white"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToggleActive(emp)}
                        className={`text-xs underline ${
                          emp.is_active
                            ? "text-red-400 hover:text-red-300"
                            : "text-emerald-400 hover:text-emerald-300"
                        }`}
                      >
                        {emp.is_active ? "Deactivate" : "Reactivate"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
